import type { TaskDetailTab } from './detailTabs.js';
import { ViewSwitch as SharedViewSwitch } from '../../ui/Tool.js';
import { useNextStep } from './nextStep.js';
import { lazy, startTransition, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { dropArrived, dropReturn, useReturn } from '../../platform/returnPath.js';
import type { InfiniteData } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Trash2, ArrowLeft, CalendarDays, CircleHelp, ListChecks, ClipboardList, Columns3, Copy, SlidersHorizontal, History, Layers, GanttChart, Keyboard, LayoutGrid, List, Rows3, MoreHorizontal, Plus, Search, Settings2, Share2, ShieldCheck, Sun, Table2, Tag, Upload, Users, Webhook, Workflow, Wrench } from 'lucide-react';
import { openPalette, openShortcuts, usePaletteActions } from '../palette/CommandPalette.js';
import { touchOnly } from '../../platform/pointer.js';
import { PEOPLE_EVENT, type PeopleRequest } from '../share/shareIntent.js';
import { Menu } from '../../ui/Menu.js';
import type { TasksToolProps } from './ProjectMenus.js';
import { DndContext, closestCorners } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import type { AreaDto, BoardDisplay, IntakeState, ProjectDto, LabelDto, ProjectMemberDto, SectionDto, TaskDto, TaskListDto, TaskPageDto, ViewFilters } from '@werft/contracts';
import { t, today } from '../../i18n/index.js';
import { ApiError } from '../../platform/api.js';
import { applyAreaOverlays, applyListOverlays, applyOverlays, useOutboxEntries, type OutboxEntry } from '../../platform/outbox.js';
import { qdefs, queryClient, useMe, useProjectSnapshot, useSavedViews, useTasks } from '../../platform/queries.js';
import { registerPrepare, saveData } from '../../platform/prepare.js';
import { Loading, SkeletonBar, ViewSkeleton, type SkeletonKind } from '../../ui/Skeleton.js';
import { presence } from '../../platform/presence.js';
import { compareTasks, realtime, useProjectLive } from '../../platform/realtime.js';
import { storage } from '../../platform/storage.js';
import { toast } from '../../platform/toast.js';
import { outbox } from '../../platform/sync.js';
import { RightsProvider, rightsOf } from '../../platform/rights.js';
import { openTaskImport, useTaskImport } from '../import/store.js';
import { viewportHeight } from '../../platform/pointer.js';
import { Shell } from '../../app/Shell.js';
import { Button, Notice } from '../../ui/ui.js';
import { Sheet } from '../../ui/Sheet.js';
import { createList } from './structureActions.js';
import { FirstTask } from './FirstTask.js';
import { ToolNav, type Tool } from './Overview.js';
import { DisplayContext, shownOfBoard, shownOfList, useDisplay } from './display.js';
import { Popover } from '../../ui/Popover.js';
import { requestView } from './viewRequest.js';
import { BUILTIN, narrows, type Filters } from './savedViews.js';
import type { TalkMode } from '../talk/TalkView.js';
// Schreibwege der lazy geladenen Werkzeuge früh anmelden: der Ausgangskorb sendet nach dem Neuladen sofort und
// verwirft Einträge ohne Handler (die Module sind klein, der Editor bleibt lazy)
import '../docs/docActions.js';
import '../talk/talkActions.js';
import '../approvals/approvalActions.js';
import { areaMatcher, newAreaId, setAreaFilter, useAreaBarCollapsed, useAreaFilter } from '../areas/areaActions.js';
import { PresenceAvatars } from './Presence.js';
import type { FilesMode } from '../files/FilesView.js';
import { selection, useSelectionEscape, useSelectionMode, useSelectionSize } from './selection.js';
import { hintStates, requests, setHintState } from '../hints/hints.js';
import type { HelpView } from '../hints/HelpSheet.js';
import { handleListDragEnd, InlineName, loadUndo, ListSection, TaskRow, useDragSensors, type RowActions } from './TaskList.js';

export type View = 'liste' | 'board' | 'tabelle' | 'kalender' | 'zeitachse';
// Ausgangskorb: Kommentar-Aktionen müssen beim Start registriert sein, sonst verwirft der Abgleich offline Geschriebenes
import './collabActions.js';
import { FirstStepHint } from './FirstStepHint.js';
import { setArchived } from './projectActions.js';
const VIEWS: View[] = ['liste', 'board', 'tabelle', 'kalender', 'zeitachse'];

/** Notizen (P12) erst bei Bedarf laden: der Editor (Tiptap, Yjs) ist groß und gehört nicht in den Start */
const TalkView = lazy(() => import('../talk/TalkView.js').then((m) => ({ default: m.TalkView })));
const DocsView = lazy(() => import('../docs/DocsView.js').then((m) => ({ default: m.DocsView })));
const IntakeView = lazy(() => import('../intake/IntakeView.js').then((m) => ({ default: m.IntakeView })));
const TimeView = lazy(() => import('../time/TimeView.js').then((m) => ({ default: m.TimeView })));
const GoalsView = lazy(() => import('../goals/GoalsView.js').then((m) => ({ default: m.GoalsView })));
const ResourcesView = lazy(() => import('../resources/ResourcesView.js').then((m) => ({ default: m.ResourcesView })));
const BudgetView = lazy(() => import('../budget/BudgetView.js').then((m) => ({ default: m.BudgetView })));
const PeopleView = lazy(() => import('../people/PeopleView.js').then((m) => ({ default: m.PeopleView })));
// ADR 0115: Entwurf „nächster Schritt“ erst beim Öffnen laden
const NextStepDialog = lazy(() => import('./NextStepDialog.js').then((m) => ({ default: m.NextStepDialog })));
const EventsView = lazy(() => import('../schedule/EventsView.js').then((m) => ({ default: m.EventsView })));
// Verlauf erst beim Öffnen laden (Kommentare, Anhänge, Erwähnungen gehören nicht ins Startpaket)
const ActivityList = lazy(() => import('./Collab.js').then((m) => ({ default: m.ActivityList })));
const RegisterView = lazy(() => import('../approvals/RegisterView.js').then((m) => ({ default: m.RegisterView })));
// Audit F13: weitere Ansichten getrennt laden und kurz nach dem Start im Leerlauf vorladen – der Wechsel bleibt sofort
const loadTable = () => import('./TableView.js');
const loadGantt = () => import('./GanttView.js');
const loadCalendar = () => import('./CalendarView.js');
const loadFiles = () => import('../files/FilesView.js');
const loadBoard = () => import('./Board.js');
// Aufgabendetails (mit Kommentaren, Anhängen, Feldern) erst beim ersten Öffnen bzw. im Leerlauf vorladen
const loadDetail = () => import('./TaskDetail.js');
const TaskDetail = lazy(() => loadDetail().then((m) => ({ default: m.TaskDetail })));
const Board = lazy(() => loadBoard().then((m) => ({ default: m.Board })));
// Selten geöffnete Fenster erst beim Öffnen laden
const MembersDialog = lazy(() => import('./MembersDialog.js').then((m) => ({ default: m.MembersDialog })));
const LabelsDialog = lazy(() => import('./PickerPanels.js').then((m) => ({ default: m.LabelsDialog })));
// 0.166.1: Felder verwalten (mit Farbwahl) erst beim Öffnen – Startpaket-Budget (a3)
const FieldsDialog = lazy(() => import('./Fields.js').then((m) => ({ default: m.FieldsDialog })));
const SelectionBar = lazy(() => import('./SelectionBar.js').then((m) => ({ default: m.SelectionBar })));
const ShareStart = lazy(() => import('../share/ShareDialog.js').then((m) => ({ default: m.ShareStart })));
const DuplicateDialog = lazy(() => import('./DuplicateDialog.js').then((m) => ({ default: m.DuplicateDialog })));
const WebhooksSheet = lazy(() => import('../integrations/WebhooksSheet.js').then((m) => ({ default: m.WebhooksSheet })));
const ReportSheet = lazy(() => import('../reports/ReportSheet.js').then((m) => ({ default: m.ReportSheet })));
// ADR 0121: Einrichtungskarte getrennt geladen (Startpaket)
const TrashDialog = lazy(() => import('./TrashDialog.js').then((m) => ({ default: m.TrashDialog })));
const ArchiveDialog = lazy(() => import('./ArchiveDialog.js').then((m) => ({ default: m.ArchiveDialog })));
const SetupCard = lazy(() => import('./SetupCard.js').then((m) => ({ default: m.SetupCard })));
const PlanDialog = lazy(() => import('../blueprints/PlanDialog.js').then((m) => ({ default: m.PlanDialog })));
const AutomationsSheet = lazy(() => import('../automations/AutomationsSheet.js').then((m) => ({ default: m.AutomationsSheet })));
/** 0.63: „Aufgaben importieren …“ – nachgeladen samt Lesern */
const TaskImportDialog = lazy(() => import('../import/TaskImportDialog.js').then((m) => ({ default: m.TaskImportDialog })));
const TableView = lazy(() => loadTable().then((m) => ({ default: m.TableView })));
const GanttView = lazy(() => loadGantt().then((m) => ({ default: m.GanttView })));
const CalendarView = lazy(() => loadCalendar().then((m) => ({ default: m.CalendarView })));
const FilesView = lazy(() => loadFiles().then((m) => ({ default: m.FilesView })));
const HelpSheet = lazy(() => import('../hints/HelpSheet.js'));
const DisplayPanel = lazy(() => import('./DisplayPanel.js'));
// 0.150/0.151: Inhalt der Menüs erst beim Öffnen bzw. im Leerlauf laden (Startpaket F13)
const loadMenus = () => import('./ProjectMenus.js');
// Bereiche (P14) nur in Projekten mit Bereichen – Leiste und Dialoge getrennt geladen (Startpaket F13)
const loadAreas = () => import('../areas/AreaBar.js');
const AreaBar = lazy(() => loadAreas().then((m) => ({ default: m.AreaBar })));
const loadAreaDialogs = () => import('../areas/AreaDialogs.js');
const AreaPickerSheet = lazy(() => loadAreaDialogs().then((m) => ({ default: m.AreaPickerSheet })));
const CreateArea = lazy(() => loadAreaDialogs().then((m) => ({ default: m.CreateArea })));
const ProjectMenuItems = lazy(() => loadMenus().then((m) => ({ default: m.ProjectMenuItems })));
const TasksToolItems = lazy(() => loadMenus().then((m) => ({ default: m.TasksToolItems })));
const StructureGuide = lazy(() => import('./StructureGuide.js').then((m) => ({ default: m.StructureGuide })));
const ToolsDialog = lazy(() => import('./ProjectDialogs.js').then((m) => ({ default: m.ToolsDialog })));
const ProjectSettingsDialog = lazy(() => import('./ProjectDialogs.js').then((m) => ({ default: m.ProjectSettingsDialog })));
const loadOverview = () => import('./ProjectOverview.js');
// 0.157: Gast-Einstieg nur für Gäste – getrennt geladen (Startpaket)
const GuestStart = lazy(() => import('./GuestStart.js').then((m) => ({ default: m.GuestStart })));
const OverviewView = lazy(() => loadOverview().then((m) => ({ default: m.OverviewView })));
let preloaded = false;
function preloadViews() {
  if (preloaded) return;
  preloaded = true;
  // 0.158 (ADR 0164): Code im Leerlauf nacheinander (nie alles gleichzeitig), nichts im Datensparmodus; Daten lädt erst
  // die Absicht (prepare) oder die Ansicht selbst
  if (saveData()) return;
  const queue = [loadDetail, loadBoard, loadOverview, loadFiles, loadMenus, loadAreas, loadUndo, loadTable, loadCalendar, loadGantt];
  const idle = (fn: () => void) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1500));
  const next = () => {
    const load = queue.shift();
    if (load) idle(() => void load().then(next, () => (preloaded = false)));
  };
  next();
}
// Vorladen mit Absicht: Zielmodul und kleine Erstabfrage je Werkzeug (gemeinsame Abfragedefinitionen)
registerPrepare('files', (pid) => [loadFiles(), queryClient.prefetchQuery(qdefs.folder(pid, null))]);
registerPrepare('overview', (pid) => [loadOverview(), queryClient.prefetchQuery(qdefs.overview(pid, today())), queryClient.prefetchQuery(qdefs.nextSteps(pid)), queryClient.prefetchQuery(qdefs.signals(pid, today()))]);
registerPrepare('docs', (pid) => [import('../docs/DocsView.js'), queryClient.prefetchQuery(qdefs.pages(pid))]);
registerPrepare('talk', () => [import('../talk/TalkView.js')]);
registerPrepare('budget', () => [import('../budget/BudgetView.js')]);
registerPrepare('resources', () => [import('../resources/ResourcesView.js')]);
registerPrepare('events', () => [import('../schedule/EventsView.js')]);
registerPrepare('time', () => [import('../time/TimeView.js')]);
registerPrepare('goals', () => [import('../goals/GoalsView.js')]);
registerPrepare('people', () => [import('../people/PeopleView.js')]);
registerPrepare('intake', () => [import('../intake/IntakeView.js')]);
registerPrepare('approvals', () => [import('../approvals/RegisterView.js')]);

/** Zuletzt genutzte Ansicht je Projekt – persönliche Vorliebe, keine Projekteinstellung (ADR 0006) */
const viewKey = (projectId: string) => `werft.view.${projectId}`;

/**
 * Projekt-Insel, Startseite = Aufgabenliste (Regel R3). Solange es nur eine Liste gibt, erscheint keine
 * Listenauswahl und keine Überschrift (Regel R6). Liste und Board sind Ansichten derselben Aufgaben (P4).
 */
export function ProjectPage({
  projectId,
  taskId,
  taskTab = 'uebersicht',
  view: viewParam,
  tool = 'tasks',
  files,
  pageId,
  newPage,
  talk,
  intake,
  register,
  events,
  persons,
  timeSection,
  shifts,
  goals,
  budget,
}: {
  projectId: string;
  taskId?: string;
  taskTab?: TaskDetailTab;
  view?: View;
  tool?: Tool;
  /** Ablage (P11): offener Ordner, Sicht, Vorschau */
  files?: { folderId: string | null; mode: FilesMode; fileId?: string };
  /** Notizen (P12): offene Seite */
  pageId?: string;
  /** Palette „Neue Seite“: gleich eine Seite anlegen */
  newPage?: boolean;
  /** Gespräche (P13): Themen oder Chat, offenes Thema, gesuchte Nachricht */
  talk?: { mode: TalkMode; topicId?: string; messageId?: string; newTopic?: boolean };
  /** Projekteingang (ADR 0064): offener Vorgang, Zustandsfilter */
  intake?: { itemId?: string; state?: IntakeState; forms?: boolean };
  /** Freigaben-Register (ADR 0071): Filter */
  register?: { filter?: 'mine' | 'open' | 'decided'; decisionId?: string; create?: boolean; topicId?: string };
  /** Termine (ADR 0072): offener Termin */
  events?: { eventId?: string };
  /** Personen & Teilnahme (ADR 0080): offener Kontakt */
  persons?: { contactId?: string; create?: boolean };
  timeSection?: 'erfassen' | 'auswerten' | 'nachweise';
  /** ADR 0083: geöffneter Einsatz */
  shifts?: { shiftId?: string; day?: string };
  /** ADR 0084: geöffnetes Ziel/Risiko */
  goals?: { goalId?: string; riskId?: string };
  /** ADR 0116: geöffnete Budgetposition */
  budget?: { lineId?: string };
}) {
  const nextStep = useNextStep();
  const navigate = useNavigate();
  const me = useMe();
  // Details erst einbinden, wenn eine Aufgabe geöffnet wurde (Startpaket, Nachprüfung R4)
  const [detailUsed, setDetailUsed] = useState(!!taskId);
  if (taskId && !detailUsed) setDetailUsed(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [hooksOpen, setHooksOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [creatingArea, setCreatingArea] = useState(false);
  // Audit 0.36 F03: „Gliedern …“ – Abschnitt, Liste oder Bereich erklärt, dazu Startgerüste
  const [structureOpen, setStructureOpen] = useState(false);
  const snap = useProjectSnapshot(projectId);
  // 0.158 (ADR 0164): Scrollposition je Projekt, Werkzeug und Ansicht – Rückkehr (Werkzeug oder Browser-Zurück) setzt
  // die Arbeit dort fort, wo sie war; ein Direktlink auf einen Inhalt holt sein Ziel selbst in den sichtbaren Bereich
  useScrollMemory(`${projectId}:${tool}:${tool === 'tasks' ? (viewParam ?? '') : ''}`, !!snap.data && !taskId);
  // 0.63: Dialog „Aufgaben importieren …“ (geöffnet aus Menüs, Palette oder der Rückfrage nach dem Einfügen)
  const importTarget = useTaskImport();
  // 0.157 (ADR 0163): Aufgaben parallel zum Projektstand laden (nicht erst danach)
  const open = useTasks(projectId, 'open', true);
  const [showDone, setShowDone] = useState(false);
  // Audit 0.36 F10: „Personen“ auch mit Absicht öffnen (Gast für eine Aufgabe einladen …)
  const [people, setPeople] = useState<false | true | PeopleRequest>(false);
  // Auswahlleiste erst laden, wenn etwas ausgewählt ist (Startpaket)
  const selectionSize = useSelectionSize();
  const selectionMode = useSelectionMode();
  useSelectionEscape(selectionSize, selectionMode);
  const [sharingOpen, setSharingOpen] = useState(false);
  useEffect(() => {
    const on = (e: Event) => {
      const r = (e as CustomEvent<PeopleRequest>).detail;
      if (r.projectId === projectId) setPeople(r);
    };
    window.addEventListener(PEOPLE_EVENT, on);
    return () => window.removeEventListener(PEOPLE_EVENT, on);
  }, [projectId]);
  // AF10: freiwillige Hilfe je Ansicht
  const [helpOpen, setHelpOpen] = useState(false);
  // AF09: Vorschlag „Abschnitte ansehen“ angenommen → Strukturhilfe öffnen
  useEffect(() => {
    const take = () => {
      if (requests.structure !== projectId) return;
      requests.structure = null;
      setStructureOpen(true);
    };
    take();
    window.addEventListener('werft:hint-request', take);
    return () => window.removeEventListener('werft:hint-request', take);
  }, [projectId]);
  const [activity, setActivity] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  /** „Abschnitt hinzufügen“ für eine Liste (Paket P8) */
  const [sectionFor, setSectionFor] = useState<string | null>(null);
  // Erledigte im Hintergrund laden: der Umschalter erscheint nur, wenn es welche gibt (Regel R2)
  const done = useTasks(projectId, 'done', !!snap.data);
  useProjectLive(projectId, snap.data?.cursor);
  const pending = useOutboxEntries(outbox, projectId);
  const areas = useMemo(() => applyAreaOverlays(snap.data?.areas ?? NO_AREAS, pending), [snap.data?.areas, pending]);
  const areaFilter = useAreaFilter(projectId, areas);
  const areaBarCollapsed = useAreaBarCollapsed();
  const areaLists = useRef(new StableLists());
  // Weitere Ansichten im Leerlauf vorladen, sobald das Projekt steht (Audit F13)
  const ready = !!snap.data;
  useEffect(() => {
    if (ready) preloadViews();
  }, [ready]);
  // Verlinkter Bereich (?bereich=…): einmal übernehmen
  useEffect(() => {
    const b = new URLSearchParams(window.location.search).get('bereich');
    if (b) setAreaFilter(projectId, b);
  }, [projectId]);
  const sensors = useDragSensors();
  const groups = useRef(new StableGroups());
  const stableLists = useRef(new StableLists());
  const stored = storage.get<View>(viewKey(projectId));
  const view: View = viewParam ?? (stored && VIEWS.includes(stored) ? stored : 'liste');
  const helpView: HelpView = tool === 'tasks' ? view : HELP_OF_TOOL[tool];
  // Schrittweise aufbauen (P5-Messung): erst die ersten Zeilen zeigen, den Rest unterbrechbar im Hintergrund – am
  // Handy erscheint die Liste so sofort, auch wenn ein großes Projekt hunderte Zeilen geladen hat.
  // Abnahme 0.49 F05: an die Ansicht gebunden – beim Zurückwechseln zur Liste ist die erste Darstellung sofort
  // gestaffelt (vorher galt `full` aus der vorigen Ansicht noch einen Durchlauf lang: alle Zeilen auf einmal)
  const [fullFor, setFullFor] = useState<View | null>(null);
  const full = fullFor === view;
  useEffect(() => {
    const id = requestAnimationFrame(() => startTransition(() => setFullFor(view)));
    return () => cancelAnimationFrame(id);
  }, [view]);

  // Auch eine über einen Link geöffnete Ansicht gilt als zuletzt genutzt
  useEffect(() => {
    if (viewParam) storage.set(viewKey(projectId), viewParam);
  }, [projectId, viewParam]);

  // Abschnitte je Liste mit stabilen Arrays (memo der Listen bleibt wirksam)
  const sectionsByList = useMemo(() => {
    const m = new Map<string, SectionDto[]>();
    for (const x of snap.data?.sections ?? []) (m.get(x.listId) ?? m.set(x.listId, []).get(x.listId)!).push(x);
    return m;
  }, [snap.data?.sections]);
  // Auswahl gilt je Projekt; beim Verlassen aufheben
  // U02: Werkzeug- oder Ansichtswechsel beendet die Auswahl – keine unsichtbare Auswahl im Hintergrund
  useEffect(() => () => selection.clear(), [projectId, tool, view]);

  // Präsenz (P9): ich bin in diesem Projekt und habe ggf. eine Aufgabe offen; verborgener Tab = nicht da
  presence.meId = me.data?.user.id ?? null;
  useEffect(() => {
    const report = () => (document.visibilityState === 'visible' ? realtime.setPresence(projectId, taskId ?? null) : realtime.setPresence(null, null));
    report();
    document.addEventListener('visibilitychange', report);
    return () => document.removeEventListener('visibilitychange', report);
  }, [projectId, taskId]);
  useEffect(() => () => realtime.setPresence(null, null), [projectId]);

  useEffect(
    () =>
      realtime.onRevoked((scope) => {
        if (scope.type === 'project' && scope.id === projectId) void navigate({ to: '/', search: { notice: 'project-gone' } });
      }),
    [projectId, navigate],
  );

  const search = (s: { aufgabe?: string; ansicht?: View }) => ({ ...(s.aufgabe ? { aufgabe: s.aufgabe } : {}), ...(s.ansicht ? { ansicht: s.ansicht } : {}) });
  // Auf der Übersicht öffnen sich Aufgaben dort (die Übersicht bleibt dahinter)
  const here =
    tool === 'overview'
      ? ('/p/$projectId/uebersicht' as const)
      : tool === 'files'
        ? ('/p/$projectId/dateien' as const)
        : tool === 'docs'
          ? ('/p/$projectId/notizen' as const)
          : tool === 'talk'
            ? ('/p/$projectId/gespraeche' as const)
            : tool === 'intake'
              ? ('/p/$projectId/eingang' as const)
              : tool === 'approvals'
                ? ('/p/$projectId/freigaben' as const)
                : tool === 'events'
                  ? ('/p/$projectId/termine' as const)
                  : tool === 'people'
                    ? ('/p/$projectId/personen' as const)
                  : tool === 'budget'
                    ? ('/p/$projectId/budget' as const)
                  : tool === 'resources'
                    ? ('/p/$projectId/einsaetze' as const)
                  : tool === 'goals'
                    ? ('/p/$projectId/ziele' as const)
                  : tool === 'time'
                    ? ('/p/$projectId/zeit' as const)
                  : ('/p/$projectId' as const);
  // Auf Übersicht und Ablage bleibt der Ort erhalten (Ordner), die Aufgabe öffnet sich darüber
  const toolSearch =
    tool === 'files' && files
      ? // F01: auch die geöffnete Datei bleibt – Schließen der Aufgabe führt zurück in ihren Arbeitsplatz
        { ...(files.folderId ? { ordner: files.folderId } : {}), ...(files.mode !== 'ablage' ? { ablage: files.mode } : {}), ...(files.fileId ? { datei: files.fileId } : {}) }
      : tool === 'docs' && pageId
        ? { seite: pageId }
        : tool === 'talk' && talk
          ? { ...(talk.mode === 'chat' ? { ansicht: 'chat' as const } : {}), ...(talk.topicId ? { thema: talk.topicId } : {}) }
          : tool === 'intake' && intake
            ? { ...(intake.itemId ? { vorgang: intake.itemId } : {}), ...(intake.state ? { zustand: intake.state } : {}) }
            : tool === 'approvals' && (register?.filter || register?.decisionId)
              ? { ...(register.filter ? { filter: register.filter } : {}), ...(register.decisionId ? { entscheidung: register.decisionId } : {}) }
              : tool === 'events' && events?.eventId
                ? { termin: events.eventId }
                : tool === 'people' && persons?.contactId
                  ? { kontakt: persons.contactId }
                : tool === 'resources' && shifts?.shiftId
                  ? { einsatz: shifts.shiftId }
                : tool === 'budget' && budget?.lineId
                  ? { position: budget.lineId }
                : tool === 'goals' && (goals?.goalId || goals?.riskId)
                  ? { ...(goals.goalId ? { ziel: goals.goalId } : {}), ...(goals.riskId ? { risiko: goals.riskId } : {}) }
                : {};
  const openTask = useCallback(
    (id: string, tab?: TaskDetailTab) =>
      void navigate({
        to: here,
        params: { projectId },
        search: { ...(tool === 'tasks' ? search({ aufgabe: id, ansicht: view }) : { ...toolSearch, aufgabe: id }), ...(tab && tab !== 'uebersicht' ? { aufgabenbereich: tab } : {}) },
      }),
    [navigate, projectId, view, here, JSON.stringify(toolSearch)], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const setTaskTab = (tab: TaskDetailTab) => void navigate({
    to: here, params: { projectId }, replace: true,
    search: { ...(tool === 'tasks' ? search({ aufgabe: taskId, ansicht: view }) : { ...toolSearch, aufgabe: taskId }), aufgabenbereich: tab === 'uebersicht' ? undefined : tab },
  });
  const clearSectionFor = useCallback(() => setSectionFor(null), []);
  const closeTask = () => void navigate({ to: here, params: { projectId }, search: tool === 'tasks' ? search({ ansicht: view }) : toolSearch });
  // Audit F16: Filter der Tabelle gelten nur dort – beim Wechsel sagen, dass jetzt wieder alles zu sehen ist
  const tableFiltered = narrows(storage.get<Partial<Filters>>(`werft.table.${projectId}`) ?? {});
  const setView = (v: View) => {
    if (view === 'tabelle' && v !== 'tabelle' && narrows(storage.get<Partial<Filters>>(`werft.table.${projectId}`) ?? {})) toast(t('view.tableFilterStays'));
    storage.set(viewKey(projectId), v);
    void navigate({ to: '/p/$projectId', params: { projectId }, search: search({ ansicht: v }), replace: true });
  };

  // P25: gespeicherte Ansichten (klein; auch für die Befehlspalette)
  const savedViews = useSavedViews(projectId);
  // Aktionen dieses Projekts in der Befehlspalette
  usePaletteActions('project', [
    {
      id: 'new-task',
      label: t('palette.newTask'),
      keywords: 'anlegen erfassen hinzufügen',
      icon: <Plus className="size-4" />,
      run: () => {
        if (view !== 'liste') setView('liste');
        setTimeout(() => document.querySelector<HTMLInputElement>(`main input[aria-label="${t('task.add')}"]`)?.focus(), 50);
      },
    },
    ...(snap.data && rightsOf(snap.data.project).edit
      ? [{ id: 'import-tasks', label: t('import.menu'), keywords: 'importieren einfügen liste excel csv planner mehrere', icon: <Upload className="size-4" />, run: () => openTaskImport({ projectId }) }]
      : []),
    ...(
      [
        ['liste', t('palette.viewList'), 'liste ansicht', <List key="l" className="size-4" />],
        ['board', t('palette.viewBoard'), 'kanban spalten ansicht', <Columns3 key="b" className="size-4" />],
        ['tabelle', t('palette.viewTable'), 'tabelle sortieren filtern spalten', <Table2 key="t" className="size-4" />],
        ['kalender', t('palette.viewCalendar'), 'kalender termine monat abo', <CalendarDays key="k" className="size-4" />],
        ['zeitachse', t('palette.viewTimeline'), 'zeitachse gantt balken abhängigkeiten planung', <GanttChart key="z" className="size-4" />],
      ] as const
    )
      .filter(([v]) => v !== view)
      .map(([v, label, keywords, icon]) => ({ id: `view-${v}`, label, keywords, icon, run: () => setView(v) })),
    // P25: Ansichten der Tabelle direkt öffnen
    ...[
      ...((snap.data?.members.length ?? 0) > 1 ? [{ id: BUILTIN.mine, name: t('views.mine'), filters: { assignee: 'me' } as ViewFilters }] : []),
      ...(savedViews.data?.items ?? []).filter((v) => !v.id.startsWith('tmp-')),
    ].map((v) => ({
      id: `saved-view-${v.id}`,
      label: t('views.palette', { name: v.name }),
      keywords: 'ansicht filter tabelle gespeichert',
      icon: <Table2 className="size-4" />,
      run: () => {
        requestView(projectId, v.id, v.filters);
        if (view !== 'tabelle') setView('tabelle');
      },
    })),
    { id: 'add-list', label: t('list.add'), keywords: 'neue liste', icon: <Plus className="size-4" />, run: () => document.querySelector<HTMLButtonElement>('main button[data-add-list]')?.click() },
    ...(snap.data?.project.tools.includes('docs') && tool !== 'docs'
      ? [{ id: 'new-page', label: t('docs.new'), keywords: 'notiz seite dokument protokoll anlegen', icon: <Plus className="size-4" />, run: () => void navigate({ to: '/p/$projectId/notizen', params: { projectId }, search: { neu: '1' as const } }) }]
      : []),
    ...(snap.data?.project.tools.includes('talk') && tool !== 'talk'
      ? [{ id: 'talk-chat', label: t('talk.chat'), keywords: 'gespräche nachricht chat', icon: <Plus className="size-4" />, run: () => void navigate({ to: '/p/$projectId/gespraeche', params: { projectId }, search: { ansicht: 'chat' as const } }) }]
      : []),
    { id: 'help', label: t('help.button'), keywords: 'hilfe einführung erklärung tipps', icon: <CircleHelp className="size-4" />, run: () => setHelpOpen(true) },
    { id: 'people', label: t('project.people'), keywords: 'mitglieder team', icon: <Users className="size-4" />, run: () => setPeople(true) },
    { id: 'report', label: t('report.menu'), keywords: 'statusbericht bericht basisplan stand drucken', icon: <ClipboardList className="size-4" />, run: () => setReportOpen(true) },
    { id: 'activity', label: t('activity.title'), keywords: 'chronik historie', icon: <History className="size-4" />, run: () => setActivity(true) },
    { id: 'labels', label: t('labels.title'), keywords: 'schlagwörter tags farben', icon: <Tag className="size-4" />, run: () => setLabelsOpen(true) },
    {
      id: 'add-section',
      label: t('section.add'),
      keywords: 'abschnitt gruppe überschrift',
      icon: <Plus className="size-4" />,
      run: () => {
        if (view !== 'liste') setView('liste');
        const first = snap.data?.lists[0];
        if (first) setSectionFor(first.id);
      },
    },
  ]);

  if (snap.error instanceof ApiError && snap.error.status === 404) {
    return (
      <Shell>
        <Notice kind="error">{t('project.gone')}</Notice>
      </Shell>
    );
  }
  if (open.isError && !open.data) {
    return (
      <Shell>
        <Notice kind="error">{t('error.generic')}</Notice>
      </Shell>
    );
  }
  // Erst mit geladener Liste anzeigen: sonst stünden offene Änderungen kurz allein da und die Liste spränge.
  // 0.157 (ADR 0163): bis dahin steht der Rahmen mit der Ladestruktur des Ziels – andere Werkzeuge warten nicht auf die Aufgaben
  if (!snap.data || (tool === 'tasks' && !open.data)) {
    const card = cachedProjectCard(projectId);
    return (
      <Shell island={{ name: snap.data?.project.name ?? card?.name ?? '', color: snap.data?.project.color ?? card?.color, id: projectId, pending: !snap.data && !card }}>
        <ProjectSkeleton tool={tool} view={view} />
      </Shell>
    );
  }
  const { project, members, statuses, labels } = snap.data;
  // Zähler (Fortschritt) getrennt von der Struktur: neue Zähler zeichnen nur die Listenköpfe neu, nicht alle Zeilen
  const allLists = stableLists.current.update(applyListOverlays(snap.data.lists, pending));
  // Bereiche (P14): gewählter Bereich filtert Listen (samt Unterbereichen); ohne Bereiche alles wie bisher
  const hasAreas = areas.length > 0;
  const matchArea = areaMatcher(areas, areaFilter);
  const lists = areaLists.current.update(hasAreas ? allLists.filter((l) => matchArea(l.areaId)) : allLists);
  const counts = new Map(snap.data.lists.map((l) => [l.id, l]));
  const pendingTasks = withPending(flatten(open.data), flatten(done.data), pending);
  const inView = hasAreas && areaFilter !== 'alle' ? new Set(lists.map((l) => l.id)) : null;
  const openTasks = inView ? pendingTasks.openTasks.filter((x) => inView.has(x.listId)) : pendingTasks.openTasks;
  const doneTasks = inView ? pendingTasks.doneTasks.filter((x) => inView.has(x.listId)) : pendingTasks.doneTasks;
  const byList = groups.current.update(pendingTasks.openTasks);
  // Auswählbar: in der Liste die offenen, auf dem Board auch die erledigten Karten
  const selectable = view === 'board' ? [...openTasks, ...doneTasks] : openTasks;
  const visible = full ? byList : firstRows(byList, lists, firstRowCount());

  const rights = rightsOf(project);
  return (
    <RightsProvider project={project}>
    <Shell
      island={{ name: project.name, color: project.color, id: project.id }}
      wide
      actions={
        <>
          <PresenceAvatars projectId={projectId} />
          {/* W09: klarer Einstieg „Projekt teilen“ im Kopf (am Handy über das Projektmenü) */}
          {project.role !== 'guest' && !project.preview && (
            <button type="button" onClick={() => setSharingOpen(true)} aria-label={t('share.project')} title={t('share.project')} data-share-project="" className="hidden min-h-9 items-center gap-2 rounded-lg px-3 text-sm text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 sm:inline-flex dark:text-zinc-300 dark:hover:bg-zinc-800">
              <Share2 className="size-4" aria-hidden /> {t('share.projectShort')}
            </button>
          )}
          <HelpButton view={helpView} onOpen={() => setHelpOpen(true)} />
          {/* Projektaktionen bleiben unabhängig von Werkzeug und Aufgabenansicht an derselben Stelle. */}

          {/* Projektmenü: ruhiger Kopf, auch am Handy (Insel-Prinzip) */}
          <Menu label={t('project.menu')} trigger={<MoreHorizontal className="size-5" aria-hidden />}>
            {() => (
              <Suspense fallback={<p className="px-3 py-2 text-sm text-zinc-500">{t('common.loading')}</p>}>
                <ProjectMenuItems
                  project={project}
                  members={members.length}
                  hasAreas={hasAreas}
                  edit={rights.edit && !rights.guest}
                  open={{ settings: () => setSettingsOpen(true), people: () => setPeople(true), sharing: () => setSharingOpen(true), external: () => void navigate({ to: '/externe-zugriffe', search: { projekt: projectId } }), tools: () => setToolsOpen(true), area: () => setCreatingArea(true), plan: () => setPlanOpen(true), auto: () => setAutoOpen(true), hooks: () => setHooksOpen(true), dup: () => setDupOpen(true), report: () => setReportOpen(true), activity: () => setActivity(true), help: () => setHelpOpen(true), archive: () => setArchiveOpen(true), unarchive: () => setArchived(project, false), trash: () => setTrashOpen(true) }}
                />
              </Suspense>
            )}
          </Menu>
        </>
      }
    >
      {/* ADR 0062: ruhiger Hinweis auf den ersten sinnvollen Schritt einer Vorlage – wegklickbar, kein Tutorial */}
      {/* ADR 0121: Leitung bekommt die Einrichtung als gezielte Schritte (erledigt = abgehakt), andere den ruhigen Hinweis */}
      {project.firstStep && project.role === 'lead' && (
        <Suspense fallback={null}>
          <SetupCard projectId={projectId} text={project.firstStep} onPlan={() => setPlanOpen(true)} onRules={() => setAutoOpen(true)} />
        </Suspense>
      )}
      {project.firstStep && project.role !== 'guest' && project.role !== 'lead' && <FirstStepHint projectId={projectId} text={project.firstStep} />}
      {/* ADR 0122: archiviert = nur lesen – sichtbar für alle, Wiederherstellen durch die Leitung */}
      {project.archived && (
        <div role="note" data-archived-banner="" className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-300 bg-zinc-100 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900">
          <Archive className="size-4 text-zinc-500" aria-hidden />
          <span className="min-w-0 flex-1">{t('archive.banner')}</span>
          {project.role === 'lead' && (
            <Button onClick={() => setArchived(snap.data.project, false)} data-restore="">
              {t('archive.restore')}
            </Button>
          )}
        </div>
      )}
      {/* W13 (Grundlage): Gäste sehen Zweck, Rechte, Laufzeit und ihre nächsten Schritte */}
      {snap.data.project.guest && (
        // F05: Platz des getrennt geladenen Gast-Einstiegs halten – die Liste darunter springt nicht
        <Suspense fallback={<Loading className="mb-4"><SkeletonBar className="h-28 w-full rounded-2xl" /></Loading>}>
          <GuestStart project={snap.data.project} members={snap.data.members} onOpenTask={openTask} />
        </Suspense>
      )}
      {/* W10: Sperre sichtbar für das Team – mit Weg zur Verwaltung (Projektleitung) */}
      {snap.data.project.externalLock && (
        <div role="status" data-external-locked="" className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200">
          <ShieldCheck className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">{t('ext.lockedBanner')}</span>
          {project.role === 'lead' && (
            <Button variant="ghost" onClick={() => void navigate({ to: '/externe-zugriffe', search: { projekt: projectId } })}>
              {t('ext.menu')}
            </Button>
          )}
        </div>
      )}
      <ReturnChip projectId={projectId} />
      <ToolNav snapshot={snap.data} current={tool} onManage={() => setToolsOpen(true)} end={tool !== 'tasks' ? undefined : <div className="sm:border-l sm:border-zinc-200 sm:pl-3 dark:sm:border-zinc-700"><div className="flex items-center gap-2">{/* Rückmeldung 31.10.: Auswählen/Darstellung stehen LINKS vom Ansichtswechsel und heben sich ab – der Umschalter bleibt beim Wechsel Liste ↔ Tabelle an seinem Platz (rechtsbündig); am Handy rechts der Auswahl */}<div className="flex items-center gap-0.5 rounded-lg bg-zinc-100 px-0.5 max-sm:order-last dark:bg-zinc-800/70" role="group" aria-label={t('tasks.viewTools')} data-task-view-tools="">{(view === 'liste' || view === 'board') && openTasks.length > 0 && !rights.guest && <SelectModeButton on={selectionMode} />}{(view === 'liste' || view === 'board') && (openTasks.length > 0 || doneTasks.length > 0) && <DisplayButton project={snap.data.project} view={view === 'board' ? 'board' : 'list'} />}{/* 0.150 (ADR 0152): Werkzeugmenü „Aufgaben“ – Struktur, Labels, Felder, Import, Auswählen; nicht mehr im Projektmenü */}{/* Klein anfangen (P5): im leeren Projekt bleibt es bei Eingabefeld und „Liste hinzufügen“ – Einfügen mehrerer Zeilen importiert dort direkt */}{(rights.manage || rights.edit) && (openTasks.length > 0 || doneTasks.length > 0 || allLists.length > 1) && <TasksToolMenu canManage={rights.manage} canSelect={openTasks.length > 0 && !rights.guest} singleList={lists.length === 1} onStructure={() => setStructureOpen(true)} onSection={() => (view !== 'liste' && setView('liste'), setTimeout(() => setSectionFor(lists[0]!.id), 0))} onLabels={() => setLabelsOpen(true)} onFields={() => setFieldsOpen(true)} onImport={() => openTaskImport({ projectId })} onSelect={() => (view !== 'liste' && view !== 'board' && setView('liste'), selection.enter())} />}</div><TaskViewSwitch projectId={projectId} view={view} onChange={setView} tableFiltered={tableFiltered} /></div></div>} />
      <div className={hasAreas ? `lg:grid lg:gap-6 ${areaBarCollapsed ? 'lg:grid-cols-[3.5rem_minmax(0,1fr)]' : 'lg:grid-cols-[13rem_minmax(0,1fr)]'}` : ''}>
      {hasAreas && (
        <Suspense fallback={<AreaBarSkeleton />}>
          <AreaBar snapshot={snap.data} areas={areas} filter={areaFilter} meId={me.data?.user.id ?? ''} />
        </Suspense>
      )}
      {nextStep && (
        <Suspense fallback={null}>
          <NextStepDialog request={nextStep} members={snap.data.members} />
        </Suspense>
      )}
      {/* Alle Werkzeuge teilen dieselben Außenkanten. Lesebreiten regelt erst der Inhalt. */}
      {/* 0.160 (ADR 0166): Werkzeugwechsel – der Rahmen bleibt, das Ziel blendet kurz ein (nur opacity, ohne Verzögerung) */}
      <div key={tool} className={`project-workspace tool-in ${tool === 'overview' ? '' : 'work-surface'}`} data-project-tool={tool} data-task-view={tool === 'tasks' ? view : undefined}>
      <Suspense fallback={<ViewSkeleton kind={skeletonKind(tool, view)} />}>
      {tool === 'time' ? (
        <TimeView snapshot={snap.data} section={timeSection} />
      ) : tool === 'goals' ? (
        <GoalsView snapshot={snap.data} areas={areas} areaFilter={areaFilter} goalId={goals?.goalId} riskId={goals?.riskId} onOpenTask={openTask} />
      ) : tool === 'resources' ? (
        <ResourcesView snapshot={snap.data} areas={areas} areaFilter={areaFilter} shiftId={shifts?.shiftId} day={shifts?.day} onOpenTask={openTask} />
      ) : tool === 'budget' ? (
        <BudgetView snapshot={snap.data} areas={areas} areaFilter={areaFilter} lineId={budget?.lineId} onOpenTask={openTask} />
      ) : tool === 'people' ? (
        <PeopleView snapshot={snap.data} areas={areas} contactId={persons?.contactId} create={persons?.create} onOpenTask={openTask} />
      ) : tool === 'events' ? (
        <EventsView snapshot={snap.data} areas={areas} areaFilter={areaFilter} eventId={events?.eventId} onOpenTask={openTask} />
      ) : tool === 'approvals' ? (
        <RegisterView snapshot={snap.data} filter={register?.filter} decisionId={register?.decisionId} create={register?.create} topicId={register?.topicId} areas={areas} areaFilter={areaFilter} onOpenTask={openTask} />
      ) : tool === 'intake' && intake ? (
        <IntakeView snapshot={snap.data} areas={areas} areaFilter={areaFilter} itemId={intake.itemId} state={intake.state} forms={intake.forms} onOpenTask={openTask} taskOpen={!!taskId} />
      ) : tool === 'talk' && talk ? (
        <Suspense fallback={<ViewSkeleton kind="split" />}>
          <TalkView snapshot={snap.data} areas={areas} areaFilter={areaFilter} meId={me.data?.user.id ?? ''} mode={talk.mode} topicId={talk.topicId} messageId={talk.messageId} startNew={talk.newTopic} onOpenTask={openTask} />
        </Suspense>
      ) : tool === 'docs' ? (
        <Suspense fallback={<ViewSkeleton kind="split" />}>
          <DocsView snapshot={snap.data} areas={areas} areaFilter={areaFilter} pageId={pageId} createNew={newPage} meId={me.data?.user.id ?? ''} meName={me.data?.user.name ?? ''} />
        </Suspense>
      ) : tool === 'files' && files ? (
        <FilesView key={me.data?.user.id ?? ''} meId={me.data?.user.id ?? ''} snapshot={snap.data} areas={areas} areaFilter={areaFilter} folderId={files.folderId} mode={files.mode} fileId={files.fileId} onOpenTask={openTask} />
      ) : tool === 'overview' ? (
        <OverviewView snapshot={snap.data} meId={me.data?.user.id ?? ''} onOpenTask={openTask} onShowActivity={() => setActivity(true)} areas={areas} areaFilter={areaFilter} />
      ) : hasAreas && !lists.length ? (
        // Audit F04: leerer Bereich – in jeder Ansicht direkt zur ersten Aufgabe
        <div className="space-y-4">
          <FirstTask projectId={projectId} areaId={newAreaId(areaFilter)} areas={areas} allLists={allLists} canCreate={rights.manage} />
          {rights.manage && <AddList projectId={projectId} lists={allLists} areaId={newAreaId(areaFilter)} />}
        </div>
      ) : view === 'tabelle' ? (
        <TableView
          full={full}
          projectId={projectId}
          lists={lists}
          allLists={allLists}
          statuses={statuses}
          members={members}
          labels={labels}
          fields={snap.data.fields}
          listFilter={inView ? [...inView] : null}
          onOpen={openTask}
          meId={me.data?.user.id ?? ''}
          projectKey={snap.data.project.key}
        />
      ) : view === 'zeitachse' ? (
        <GanttView projectId={projectId} lists={lists} listFilter={inView ? [...inView] : null} onOpen={openTask} projectKey={snap.data.project.key} />
      ) : view === 'kalender' ? (
        <CalendarView projectId={projectId} lists={lists} listFilter={inView ? [...inView] : null} onOpen={openTask} showEvents={snap.data.project.tools.includes('events')} />
      ) : view === 'board' ? (
        <DisplayScope project={snap.data.project} view="board">
        <Board
          projectId={projectId}
          statuses={statuses}
          lists={lists}
          openTasks={openTasks}
          doneTasks={doneTasks}
          members={members}
          labels={labels}
          hasMoreDone={!!done.hasNextPage}
          onMoreDone={() => void done.fetchNextPage()}
          onOpen={openTask}
        />
        </DisplayScope>
      ) : (
        <DisplayScope project={snap.data.project} view="list">
        <div className="space-y-6">
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={(e) => handleListDragEnd(e, openTasks, snap.data.sections)}>
            {/* Etappe D: erste Stufe nur Listen mit sichtbaren Zeilen – 20 Listenköpfe samt Eingabefeld kosten am Handy spürbar */}
            {(full ? lists : lists.filter((l, i) => i === 0 || (visible.get(l.id)?.length ?? 0) > 0)).map((list) => (
              <ListSection
                key={list.id}
                projectId={projectId}
                list={list}
                lists={lists}
                doneCount={counts.get(list.id)?.doneCount ?? 0}
                totalCount={(counts.get(list.id)?.doneCount ?? 0) + (counts.get(list.id)?.openCount ?? 0)}
                sections={sectionsByList.get(list.id) ?? NO_SECTIONS}
                labels={labels}
                showHeading={lists.length > 1 || hasAreas}
                hasAreas={hasAreas}
                tasks={visible.get(list.id) ?? NO_TASKS}
                staged={!full}
                members={members}
                autoFocus={lists.length === 1 && openTasks.length === 0}
                addingSection={sectionFor === list.id}
                onSectionAddDone={clearSectionFor}
                onOpen={openTask}
              />
            ))}
          </DndContext>
          {open.hasNextPage && (
            <Button variant="ghost" onClick={() => void open.fetchNextPage()} busy={open.isFetchingNextPage}>
              {t('task.more')}
            </Button>
          )}
          {rights.manage && <AddList projectId={projectId} lists={allLists} areaId={newAreaId(areaFilter)} onGuide={() => setStructureOpen(true)} />}
          <KeysHint />
          {doneTasks.length > 0 && (
            <div>
              <Button variant="ghost" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}>
                {showDone ? t('task.hideDone') : t('task.showDone', { n: done.hasNextPage ? `${doneTasks.length}+` : doneTasks.length })}
              </Button>
              {showDone && (
                <DndContext>
                  <SortableContext items={doneTasks.map((x) => x.id)}>
                    <DoneRows tasks={doneTasks} members={members} labels={labels} list={lists[0]!} onOpen={openTask} />
                  </SortableContext>
                </DndContext>
              )}
              {showDone && done.hasNextPage && (
                <Button variant="ghost" onClick={() => void done.fetchNextPage()} busy={done.isFetchingNextPage}>
                  {t('task.more')}
                </Button>
              )}
            </div>
          )}
        </div>
        </DisplayScope>
      )}
      </Suspense>
      </div>
      </div>
      {hasAreas && (
        <Suspense fallback={null}>
          <AreaPickerSheet areas={areas} />
        </Suspense>
      )}
      {creatingArea && (
        <Suspense fallback={null}>
          <CreateArea snapshot={snap.data} areas={areas} meId={me.data?.user.id ?? ''} parentId={null} onClose={() => setCreatingArea(false)} />
        </Suspense>
      )}
      {fieldsOpen && (
        <Suspense fallback={null}>
          <FieldsDialog open onClose={() => setFieldsOpen(false)} snapshot={snap.data} />
        </Suspense>
      )}
      {importTarget?.projectId === projectId && (
        <Suspense fallback={null}>
          <TaskImportDialog target={importTarget} snapshot={snap.data} guest={rights.guest} />
        </Suspense>
      )}
      {trashOpen && (
        <Suspense fallback={null}>
          <TrashDialog project={snap.data.project} onClose={() => setTrashOpen(false)} />
        </Suspense>
      )}
      {archiveOpen && (
        <Suspense fallback={null}>
          <ArchiveDialog project={snap.data.project} onClose={() => setArchiveOpen(false)} />
        </Suspense>
      )}
      {autoOpen && (
        <Suspense fallback={null}>
          <AutomationsSheet snapshot={snap.data} onClose={() => setAutoOpen(false)} />
        </Suspense>
      )}
      {hooksOpen && (
        <Suspense fallback={null}>
          <WebhooksSheet projectId={projectId} onClose={() => setHooksOpen(false)} />
        </Suspense>
      )}
      {reportOpen && (
        <Suspense fallback={null}>
          <ReportSheet projectId={projectId} onClose={() => setReportOpen(false)} />
        </Suspense>
      )}
      {planOpen && (
        <Suspense fallback={null}>
          <PlanDialog projectId={projectId} workspaceId={snap.data.project.workspaceId} members={snap.data.members} onClose={() => setPlanOpen(false)} />
        </Suspense>
      )}
      {dupOpen && (
        <Suspense fallback={null}>
          <DuplicateDialog open onClose={() => setDupOpen(false)} projectId={projectId} workspaceId={project.workspaceId} name={project.name} />
        </Suspense>
      )}
      {helpOpen && (
        <Suspense fallback={null}>
          <HelpSheet
            view={helpView}
            onClose={() => {
              setHelpOpen(false);
              void setHintState(`intro:${helpView}`, 'done').catch(() => {});
            }}
          />
        </Suspense>
      )}
      {structureOpen && (
        <Suspense fallback={null}>
          <StructureGuide
            open
            onClose={() => setStructureOpen(false)}
            snapshot={snap.data}
            lists={allLists}
            onSection={() => {
              if (view !== 'liste') setView('liste');
              const first = lists[0] ?? allLists[0];
              if (first) setTimeout(() => setSectionFor(first.id), 50);
            }}
            onList={() => {
              if (view !== 'liste') setView('liste');
              setTimeout(() => document.querySelector<HTMLButtonElement>('main button[data-add-list]')?.click(), 80);
            }}
            onArea={project.role === 'lead' ? () => setCreatingArea(true) : null}
          />
        </Suspense>
      )}
      {(toolsOpen || settingsOpen) && (
        <Suspense fallback={null}>
          <ToolsDialog open={toolsOpen} onClose={() => setToolsOpen(false)} snapshot={snap.data} />
          <ProjectSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} snapshot={snap.data} />
        </Suspense>
      )}
      {tool === 'tasks' && (view === 'liste' || view === 'board') && !rights.guest && (selectionSize > 0 || selectionMode) && (
        <Suspense fallback={null}>
          <SelectionBar tasks={selectable} members={members} labels={labels} lists={lists} />
        </Suspense>
      )}
      <RetainSelection tasks={selectable} />
      {/* Nachprüfung R4: Details erst ab der ersten geöffneten Aufgabe einbinden (sonst lädt der Chunk beim Start mit);
          danach bleiben sie eingebunden, damit das Schließen animiert */}
      {detailUsed && (
        <Suspense fallback={null}>
          <TaskDetail tab={taskTab} onTabChange={setTaskTab} projectId={projectId} taskId={taskId} snapshot={{ ...snap.data, lists: allLists }} onClose={closeTask} onOpenTask={openTask} />
        </Suspense>
      )}
      {labelsOpen && (
        <Suspense fallback={null}>
          <LabelsDialog open onClose={() => setLabelsOpen(false)} projectId={projectId} labels={labels} />
        </Suspense>
      )}
      <Sheet open={activity} onClose={() => setActivity(false)} title={t('activity.title')}>
        {activity && (
          <Suspense fallback={null}>
          <ActivityList
            projectId={projectId}
            members={members}
            onOpenTask={(id) => {
              setActivity(false);
              openTask(id);
            }}
          />
          </Suspense>
        )}
      </Sheet>
      {people && (
        <Suspense fallback={null}>
          <MembersDialog open onClose={() => setPeople(false)} snapshot={snap.data} request={typeof people === 'object' ? people : undefined} />
        </Suspense>
      )}
      {sharingOpen && (
        <Suspense fallback={null}>
          <ShareStart
            project={snap.data.project}
            onClose={() => setSharingOpen(false)}
            onPeople={(focus) => {
              setSharingOpen(false);
              setPeople({ projectId, focus });
            }}
          />
        </Suspense>
      )}
    </Shell>
    </RightsProvider>
  );
}

/** Umschalter Liste/Board – beide zeigen dieselben Aufgaben; die URL (`?ansicht=board`) ist teilbar */
/** W03/W04: wirksame Darstellung (Projektvorgabe, persönlich, Vorschau) für Liste bzw. Board */
function DisplayScope({ project, view, children }: { project: ProjectDto; view: 'list' | 'board'; children: ReactNode }) {
  const d = useDisplay(project, view as 'list');
  const shown = useMemo(() => (view === 'board' ? shownOfBoard(d.value as unknown as BoardDisplay) : shownOfList(d.value)), [d.value, view]);
  return <DisplayContext.Provider value={shown}>{children}</DisplayContext.Provider>;
}

/**
 * 0.150 (ADR 0152): Werkzeugmenü „Aufgaben“ – gleiche Gruppenordnung wie überall: Arbeiten (Auswählen, Import),
 * Einrichten (Gliedern, Abschnitt, Labels, Felder). Leere Gruppen verschwinden samt Trenner.
 */
function TasksToolMenu(p: TasksToolProps) {
  return (
    <Menu label={t('tasks.toolMenu')} trigger={<Wrench className="size-4" aria-hidden />}>
      {() => (
        <Suspense fallback={<p className="px-3 py-2 text-sm text-zinc-500">{t('common.loading')}</p>}>
          <TasksToolItems {...p} />
        </Suspense>
      )}
    </Menu>
  );
}

/** „Darstellung“ – markiert, wenn persönlich angepasst; das Bedienfeld lädt erst beim Öffnen */
/** U02: ausdrücklicher Einstieg „Aufgaben auswählen“ – auch ohne Listenkopf (eine Liste) und am Handy */
function SelectModeButton({ on }: { on: boolean }) {
  return (
    <button
      type="button"
      onClick={() => (on ? selection.clear() : selection.enter())}
      aria-pressed={on}
      aria-label={t('select.enter')}
      title={t('select.enter')}
      data-select-mode=""
      className={`grid size-11 shrink-0 place-items-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 ${on ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]' : 'text-zinc-600 dark:text-zinc-400'}`}
    >
      <ListChecks className="size-4" aria-hidden />
    </button>
  );
}

function DisplayButton({ project, view }: { project: ProjectDto; view: 'list' | 'board' }) {
  const [open, setOpen] = useState(false);
  const d = useDisplay(project, view as 'list');
  const label = d.personal ? t('display.buttonPersonal') : t('display.button');
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={t('display.button')}
      anchor={
        <button type="button" onClick={() => setOpen(true)} aria-label={label} title={label} data-display-button="" className="relative grid size-11 shrink-0 place-items-center rounded-lg text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800">
          <SlidersHorizontal className="size-4" aria-hidden />
          {d.personal && <span aria-hidden className="absolute top-2 right-2 size-1.5 rounded-full bg-[var(--color-accent)]" />}
        </button>
      }
    >
      {open && (
        <Suspense fallback={<p className="p-1 text-zinc-500">{t('common.loading')}</p>}>
          <DisplayPanel project={project} view={view} onDone={() => setOpen(false)} />
        </Suspense>
      )}
    </Popover>
  );
}

/** ADR 0132: Aufgabenansichten über den gemeinsamen Ansichtswechsel – echte Adressen (`?ansicht=`), daher Links */
function TaskViewSwitch({ projectId, view, onChange, tableFiltered }: { projectId: string; view: View; onChange: (v: View) => void; tableFiltered?: boolean }) {
  const href = (v: View) => `/p/${projectId}${v === 'liste' ? '' : `?ansicht=${v}`}`;
  return (
    <SharedViewSwitch
      semantics="nav"
      label={t('view.label')}
      value={view}
      onChange={onChange}
      data-task-views=""
      items={[
        { id: 'liste' as View, label: t('view.list'), icon: <List className="size-4" aria-hidden />, href: href('liste') },
        { id: 'board' as View, label: t('view.board'), icon: <Columns3 className="size-4" aria-hidden />, href: href('board') },
        // Audit F16: die Tabelle hat eigene Filter – sichtbar, auch wenn man gerade woanders ist
        { id: 'tabelle' as View, label: t('view.table'), icon: <Rows3 className="size-4" aria-hidden />, href: href('tabelle'), marker: tableFiltered ? t('view.filterActive') : undefined },
        { id: 'kalender' as View, label: t('view.calendar'), icon: <CalendarDays className="size-4" aria-hidden />, href: href('kalender') },
        { id: 'zeitachse' as View, label: t('view.timeline'), icon: <GanttChart className="size-4" aria-hidden />, href: href('zeitachse') },
      ]}
    />
  );
}

const HELP_OF_TOOL: Record<Exclude<Tool, 'tasks'>, HelpView> = { overview: 'uebersicht', docs: 'notizen', files: 'dateien', talk: 'gespraeche', intake: 'eingang', approvals: 'freigaben', events: 'termine', people: 'personen', budget: 'budget', resources: 'einsaetze', goals: 'ziele', time: 'zeit' };

/**
 * AF10: „Was kann ich hier machen?“ – ein ruhiger Knopf im Projektkopf (am Handy nur im Projektmenü). Beim ersten Besuch einer Ansicht trägt er
 * einen kleinen Punkt (einmal je Person und Ansicht, bis die Hilfe geöffnet wurde); er öffnet nichts von selbst.
 */
function HelpButton({ view, onOpen }: { view: HelpView; onOpen: () => void }) {
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    let live = true;
    setFresh(false);
    void hintStates().then((s) => live && setFresh(s.tips && !s.states[`intro:${view}`]));
    return () => {
      live = false;
    };
  }, [view]);
  return (
    <button
      type="button"
      onClick={() => {
        setFresh(false);
        onOpen();
      }}
      aria-label={fresh ? `${t('help.button')} – ${t('help.new')}` : t('help.button')}
      title={fresh ? t('help.new') : t('help.button')}
      data-help-new={fresh || undefined}
      className="relative hidden size-9 items-center justify-center rounded-lg text-zinc-500 sm:inline-flex hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
    >
      <CircleHelp className="size-5" aria-hidden />
      {fresh && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-[var(--color-accent)]" aria-hidden />}
    </button>
  );
}

/** „Liste hinzufügen“ – unauffällig unter den Listen (Bedienvertrag „zweite Liste“) */
function AddList({ projectId, lists, areaId, onGuide }: { projectId: string; lists: ReturnType<typeof applyListOverlays>; areaId: string | null; onGuide?: () => void }) {
  const [adding, setAdding] = useState(false);
  if (adding) {
    return (
      <div className="space-y-1">
        <div className="flex">
          <InlineName
            value=""
            label={t('list.namePlaceholder')}
            onDone={(name) => {
              setAdding(false);
              if (name) void createList(projectId, name, lists, areaId);
            }}
          />
        </div>
        {/* Audit 0.36 F03: beim Ausbauen kurz erklären, wann eine Liste hilft – und was es sonst gibt */}
        {onGuide && (
          <p className="flex flex-wrap items-center gap-x-1 px-1 text-xs text-zinc-500">
            {t('structure.listTip')}
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => (setAdding(false), onGuide())} className="min-h-9 rounded px-1 font-medium text-[var(--color-accent)] underline-offset-2 hover:underline">
              {t('structure.which')}
            </button>
          </p>
        )}
      </div>
    );
  }
  return (
    <Button variant="ghost" data-add-list="" onClick={() => setAdding(true)} className="text-zinc-500">
      <Plus className="size-4" aria-hidden /> {t('list.add')}
    </Button>
  );
}

/**
 * Anzeige = Serverstand + offene Änderungen aus dem Ausgangskorb (auch nach Neuladen). Was durch eine offene
 * Änderung erledigt oder wieder geöffnet wird, wechselt sofort den Abschnitt.
 */
function withPending(open: TaskDto[], done: TaskDto[], pending: OutboxEntry[]) {
  if (!pending.length) return { openTasks: open, doneTasks: done };
  const doneIds = new Set(done.map((x) => x.id));
  // Unteraufgaben (auch vorläufige) stehen bei ihrer Aufgabe, nicht in der Liste
  const openIds = new Set(open.map((o) => o.id));
  const all = applyOverlays([...open, ...done.filter((x) => !openIds.has(x.id))], pending).filter((x) => !x.parentId);
  const openTasks = all.filter((x) => !x.completedAt).sort(compareTasks);
  // Erledigte: Reihenfolge des Servers; frisch Erledigte oben
  const order = new Map(done.map((x, i) => [x.id, i]));
  const doneTasks = all
    .filter((x) => x.completedAt)
    .sort((a, b) => (doneIds.has(a.id) ? (order.get(a.id) ?? 0) : -1) - (doneIds.has(b.id) ? (order.get(b.id) ?? 0) : -1));
  return { openTasks, doneTasks };
}

/* ---------- 0.158 (ADR 0164): Scrollposition je Arbeitsort ---------- */
const scrollMemory = new Map<string, number>();
function useScrollMemory(key: string, ready: boolean) {
  const current = useRef(key);
  current.current = key;
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => scrollMemory.set(current.current, window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => (window.removeEventListener('scroll', onScroll), cancelAnimationFrame(raf));
  }, []);
  useLayoutEffect(() => {
    if (!ready) return;
    const y = scrollMemory.get(key);
    if (y == null) return;
    // nach dem Zurücksetzen durch den Router (nächster Frame) die gemerkte Stelle wieder einnehmen
    const raf = requestAnimationFrame(() => window.scrollTo({ top: y }));
    return () => cancelAnimationFrame(raf);
  }, [key, ready]);
}

/* ---------- 0.157 (ADR 0163): beständiger Rahmen beim ersten Aufruf ---------- */

/** Projektname aus den Projektkarten der Lobby (falls schon geladen) – der Kopf steht damit sofort */
function cachedProjectCard(projectId: string): { name: string; color: string | null } | null {
  for (const [, data] of queryClient.getQueriesData<{ items: { id: string; name: string; color: string | null }[] }>({ queryKey: ['ws'] })) {
    const hit = data?.items?.find?.((p) => p.id === projectId);
    if (hit) return hit;
  }
  return null;
}

export function skeletonKind(tool: Tool, view: View): SkeletonKind {
  if (tool === 'tasks') return view === 'board' ? 'board' : view === 'tabelle' ? 'table' : view === 'kalender' ? 'calendar' : 'list';
  if (tool === 'overview') return 'overview';
  if (tool === 'files') return 'list';
  if (tool === 'docs' || tool === 'talk' || tool === 'intake') return 'split';
  if (tool === 'budget' || tool === 'time') return 'table';
  return 'cards';
}

/** Werkzeugleiste und Arbeitsfläche in ihren Maßen, solange Projektstand oder Ziel noch laden */
function ProjectSkeleton({ tool, view }: { tool: Tool; view: View }) {
  return (
    <>
      {/* Höhen wie die echte Werkzeugleiste: Rechner eine Zeile, Handy Werkzeugzeile(n) + bei Aufgaben die Ansichtszeile */}
      <div aria-hidden className={`project-tools mb-6 flex min-h-14 flex-wrap items-center gap-x-2 gap-y-1 border-b border-zinc-200 pb-2 dark:border-zinc-700 ${tool === 'tasks' ? 'max-sm:h-36' : 'max-sm:h-24'} max-sm:content-start`}>
        {['w-24', 'w-20', 'w-16', 'w-20'].map((w, i) => (
          <SkeletonBar key={i} className={`h-8 ${w}`} />
        ))}
      </div>
      <div className={`project-workspace ${tool === 'overview' ? '' : 'work-surface'}`} data-project-tool={tool}>
        <ViewSkeleton kind={skeletonKind(tool, view)} />
      </div>
    </>
  );
}

/** Bereichsleiste lädt getrennt: mobil die Höhe der Auswahl, am Rechner die Spalte reservieren */
function AreaBarSkeleton() {
  return (
    <>
      <div aria-hidden className="mb-3 flex h-11 items-center gap-2 lg:hidden">
        <SkeletonBar className="size-4" />
        <SkeletonBar className="h-11 flex-1" />
      </div>
      <div aria-hidden className="hidden lg:block" />
    </>
  );
}

function flatten(d: InfiniteData<TaskPageDto, unknown> | undefined): TaskDto[] {
  return d ? d.pages.flatMap((p) => p.items) : [];
}

/** Erledigte: feste Reihenfolge (zuletzt erledigt oben), Enter öffnet Details; Zeilen wie in der Liste */
/** Ausgewählte, die nicht mehr sichtbar sind (erledigt, gelöscht, verschoben), fallen aus der Auswahl */
function RetainSelection({ tasks }: { tasks: TaskDto[] }) {
  useEffect(() => {
    if (selection.size) selection.retain(new Set(tasks.map((x) => x.id)));
  }, [tasks]);
  return null;
}

function DoneRows({ tasks, members, labels, list, onOpen }: { tasks: TaskDto[]; members: ProjectMemberDto[]; labels: LabelDto[]; list: TaskListDto; onOpen: (id: string) => void }) {
  const open = useRef(onOpen);
  open.current = onOpen;
  const actions = useMemo<RowActions>(
    () => ({
      edit: (id) => open.current(id),
      editDone: () => {},
      focus: () => {},
      keyDown: (e, task) => {
        if (e.target === e.currentTarget && e.key === 'Enter') open.current(task.id);
      },
      open: (id) => open.current(id),
      move: () => {},
      moveToList: () => {},
      picker: () => {},
      position: () => ({ index: 0, count: 0 }),
      sections: () => NO_SECTIONS,
      moveToSection: () => {},
    }),
    [],
  );
  return (
    <ul className="mt-2 divide-y divide-zinc-100 dark:divide-zinc-800">
      {tasks.map((task, index) => (
        <TaskRow key={task.id} task={task} members={members} labels={labels} lists={NO_LISTS} currentList={list} tabbable={index === 0} editing={false} picker={null} actions={actions} fixedOrder />
      ))}
    </ul>
  );
}
const NO_LISTS: TaskListDto[] = [];

const NO_TASKS: TaskDto[] = [];
const NO_SECTIONS: SectionDto[] = [];

/**
 * Aufgaben je Liste mit stabilen Arrays: bleibt der Inhalt einer Liste gleich, bleibt auch das Array gleich – so
 * zeichnet `memo` nur die Liste neu, in der sich etwas geändert hat (P5-Messung).
 */
class StableGroups {
  private prev = new Map<string, TaskDto[]>();
  update(tasks: TaskDto[]): Map<string, TaskDto[]> {
    const next = new Map<string, TaskDto[]>();
    for (const x of tasks) (next.get(x.listId) ?? next.set(x.listId, []).get(x.listId)!).push(x);
    for (const [id, arr] of next) {
      const old = this.prev.get(id);
      if (old && old.length === arr.length && old.every((x, i) => x === arr[i])) next.set(id, old);
    }
    this.prev = next;
    return next;
  }
}

/** Listen ohne Zähler, gleiches Array solange sich Struktur (Name, Reihenfolge, Version) nicht ändert */
class StableLists {
  private prev: TaskListDto[] = [];
  private key = '';
  update(lists: TaskListDto[]): TaskListDto[] {
    const key = lists.map((l) => `${l.id}|${l.name}|${l.position}|${l.version}|${l.areaId}`).join('\n');
    if (key !== this.key) {
      this.key = key;
      this.prev = lists.map(({ openCount: _o, doneCount: _d, ...l }) => l);
    }
    return this.prev;
  }
}

/**
 * Etappe D (Leistung am Handy): zuerst nur so viele Zeilen, wie in den Bildschirm passen (+ Puffer), höchstens 40 – der
 * Rest folgt unterbrechbar. Am Handy (≈ 18 Zeilen statt 40) halbiert das die Arbeit bis zur ersten sichtbaren Zeile.
 */
const firstRowCount = () => Math.min(40, Math.ceil(viewportHeight() / 44) + 6);
const NO_AREAS: AreaDto[] = [];

/** Die ersten n Zeilen über alle Listen in ihrer Reihenfolge */
function firstRows(byList: Map<string, TaskDto[]>, lists: TaskListDto[], n: number): Map<string, TaskDto[]> {
  const out = new Map<string, TaskDto[]>();
  let left = n;
  for (const l of lists) {
    const all = byList.get(l.id) ?? NO_TASKS;
    out.set(l.id, left >= all.length ? all : all.slice(0, Math.max(0, left)));
    left -= all.length;
  }
  return out;
}

/**
 * Audit 0.36 F05: Tastaturhilfe genau einmal je Ansicht (nicht unter jeder Liste) und zugeklappt. Für Screenreader
 * beschreibt derselbe Text alle Listen (`aria-describedby="task-keys"`). Am reinen Touchgerät nur unsichtbar.
 */
function KeysHint() {
  const [open, setOpen] = useState(false);
  if (touchOnly())
    return (
      <p id="task-keys" className="sr-only">
        {t('task.keys')}
      </p>
    );
  return (
    <div className="text-xs text-zinc-500">
      <button type="button" aria-expanded={open} aria-controls="task-keys" onClick={() => setOpen((v) => !v)} className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 hover:text-zinc-700 dark:hover:text-zinc-300">
        <Keyboard className="size-4" aria-hidden /> {t('keys.title')}
      </button>
      <p id="task-keys" className={open ? 'px-2 pb-2' : 'sr-only'}>
        {t('task.keys')}{' '}
        {open && (
          <button type="button" onClick={openShortcuts} className="underline underline-offset-2 hover:text-zinc-700 dark:hover:text-zinc-300">
            {t('keys.all')}
          </button>
        )}
      </p>
    </div>
  );
}

/** F01: sichtbarer Rückweg nach einem Sprung in ein anderes Werkzeug („Zurück zur Notiz“) */
function ReturnChip({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  const back = useReturn(projectId, href);
  // Über Browser-Zurück schon angekommen: Eintrag erledigt
  useEffect(() => {
    dropArrived(projectId, href);
  }, [projectId, href]);
  if (!back) return null;
  return (
    <div className="mb-2" data-return="">
      <button
        type="button"
        onClick={() => {
          dropReturn(back.href);
          void navigate({ href: back.href });
        }}
        className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-[var(--color-accent)] hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        <ArrowLeft className="size-4" aria-hidden /> {t(`back.${back.kind}`)}
      </button>
    </div>
  );
}

